import {
  ANIMALIA_LIST_FILTER_KEYS,
  AnimaliaListFilters,
  AnimaliaTaxonomyFilterKey,
} from '../../domain/ports/AnimaliaListRepositoryPort';

export interface AnimaliaNavigableTaxonomyDTO {
  filterKey: AnimaliaTaxonomyFilterKey;
  label: string;
  value: string;
}

export interface AnimaliaCanonicalTaxonomyRouteDTO extends AnimaliaNavigableTaxonomyDTO {}

export interface AnimaliaResolvedListingFiltersDTO {
  primaryTaxonomy: AnimaliaCanonicalTaxonomyRouteDTO | null;
  filters: AnimaliaListFilters | undefined;
}

export const TAXONOMY_LABELS: Record<AnimaliaTaxonomyFilterKey, string> = {
  kingdom: 'Reino',
  phylum: 'Filo',
  class: 'Clase',
  order: 'Orden',
  family: 'Familia',
  genus: 'Genero',
  species: 'Especie',
};

export function isAnimaliaTaxonomyFilterKey(value: string): value is AnimaliaTaxonomyFilterKey {
  return ANIMALIA_LIST_FILTER_KEYS.includes(value as AnimaliaTaxonomyFilterKey);
}

export function createAnimaliaNavigableTaxonomy(
  filterKey: AnimaliaTaxonomyFilterKey,
  value: string | null | undefined,
): AnimaliaNavigableTaxonomyDTO | null {
  const normalizedValue = normalizeAnimaliaTaxonomyValue(value);

  if (normalizedValue === null) {
    return null;
  }

  return {
    filterKey,
    label: TAXONOMY_LABELS[filterKey],
    value: normalizedValue,
  };
}

export function buildAnimaliaTaxonomyCanonicalRoute(
  filterKey: AnimaliaTaxonomyFilterKey,
  value: string,
): string {
  return `/${encodeURIComponent(filterKey)}/${encodeURIComponent(value)}`;
}

export function parseAnimaliaTaxonomyCanonicalRoute(
  path: string,
): AnimaliaCanonicalTaxonomyRouteDTO | null {
  const normalizedPath = path.trim();

  if (normalizedPath === '' || normalizedPath === '/') {
    return null;
  }

  const segments = normalizedPath.split('/').filter((segment) => segment.trim() !== '');

  if (segments.length !== 2) {
    return null;
  }

  const [taxonomyKey, rawValue] = segments;

  if (!isAnimaliaTaxonomyFilterKey(taxonomyKey)) {
    return null;
  }

  return createAnimaliaNavigableTaxonomy(taxonomyKey, decodeURIComponent(rawValue));
}

export function resolveAnimaliaListingFiltersFromLocation(
  path: string,
  query: ReadonlyMap<string, string>,
): AnimaliaResolvedListingFiltersDTO {
  const primaryTaxonomy = parseAnimaliaTaxonomyCanonicalRoute(path);
  const queryFilters = animaliaListFiltersFromQuery(query);

  if (!primaryTaxonomy) {
    return {
      primaryTaxonomy: null,
      filters: queryFilters,
    };
  }

  return {
    primaryTaxonomy,
    filters: normalizeAnimaliaListFilters({
      ...(queryFilters ?? {}),
      [primaryTaxonomy.filterKey]: primaryTaxonomy.value,
    }),
  };
}

export function normalizeAnimaliaListFilters(
  filters: AnimaliaListFilters | undefined,
): AnimaliaListFilters | undefined {
  const normalizedFilters = ANIMALIA_LIST_FILTER_KEYS.reduce<AnimaliaListFilters>((accumulator, filterKey) => {
    const normalizedValue = normalizeAnimaliaTaxonomyValue(filters?.[filterKey]);

    if (normalizedValue !== null) {
      accumulator[filterKey] = normalizedValue;
    }

    return accumulator;
  }, {});

  return Object.keys(normalizedFilters).length > 0 ? normalizedFilters : undefined;
}

export function serializeAnimaliaListFilters(filters: AnimaliaListFilters | undefined): string {
  const normalizedFilters = normalizeAnimaliaListFilters(filters);

  if (!normalizedFilters) {
    return '';
  }

  return ANIMALIA_LIST_FILTER_KEYS.reduce((signature, filterKey) => {
    const value = normalizedFilters[filterKey];

    if (!value) {
      return signature;
    }

    return `${signature}${filterKey}:${value}|`;
  }, '');
}

export function toAnimaliaNavigableFilters(
  filters: AnimaliaListFilters | undefined,
): ReadonlyArray<AnimaliaNavigableTaxonomyDTO> {
  const normalizedFilters = normalizeAnimaliaListFilters(filters);

  if (!normalizedFilters) {
    return [];
  }

  return ANIMALIA_LIST_FILTER_KEYS.reduce<AnimaliaNavigableTaxonomyDTO[]>((accumulator, filterKey) => {
    const filter = createAnimaliaNavigableTaxonomy(filterKey, normalizedFilters[filterKey]);

    if (filter) {
      accumulator.push(filter);
    }

    return accumulator;
  }, []);
}

export function animaliaListFiltersToQueryRecord(
  filters: AnimaliaListFilters | undefined,
): Readonly<Record<string, string>> {
  const normalizedFilters = normalizeAnimaliaListFilters(filters);

  if (!normalizedFilters) {
    return {};
  }

  return ANIMALIA_LIST_FILTER_KEYS.reduce<Record<string, string>>((accumulator, filterKey) => {
    const value = normalizedFilters[filterKey];

    if (value) {
      accumulator[filterKey] = value;
    }

    return accumulator;
  }, {});
}

export function animaliaListFiltersFromQuery(
  query: ReadonlyMap<string, string>,
): AnimaliaListFilters | undefined {
  const filters = ANIMALIA_LIST_FILTER_KEYS.reduce<AnimaliaListFilters>((accumulator, filterKey) => {
    const value = query.get(filterKey);

    if (typeof value === 'string' && value.trim() !== '') {
      accumulator[filterKey] = value;
    }

    return accumulator;
  }, {});

  return Object.keys(filters).length > 0 ? filters : undefined;
}

export function stringifyAnimaliaListFiltersQuery(filters: AnimaliaListFilters | undefined): string {
  const searchParams = new URLSearchParams(animaliaListFiltersToQueryRecord(filters));
  return searchParams.toString();
}

export function buildAnimaliaListingRoute(
  path: string,
  query?: Readonly<Record<string, string | undefined>>,
): string {
  const searchParams = new URLSearchParams();

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (typeof value === 'string' && value.trim() !== '') {
        searchParams.set(key, value);
      }
    });
  }

  const queryString = searchParams.toString();

  return `${path}${queryString ? `?${queryString}` : ''}`;
}

function normalizeAnimaliaTaxonomyValue(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmedValue = value.trim();

  return trimmedValue !== '' ? trimmedValue : null;
}
